import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useTranslator } from '@/hooks/useTranslator';
import type { UserMenuCredits } from './types';

/** Monthly AI credit balance, with the breakdown behind the info icon. */
export function UserMenuAiCredits({
  credits,
}: {
  credits: UserMenuCredits | null;
}) {
  const { getTranslation } = useTranslator({ page: 'appHeader' });
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  return (
    <div className='px-3 py-3.5'>
      <div className='flex items-center justify-between mb-2'>
        <div className='flex items-center gap-1.5'>
          <span className='text-sm font-semibold'>
            {getTranslation('tracingAiCredits')}
          </span>
          {credits && (
            <Popover open={breakdownOpen} onOpenChange={setBreakdownOpen}>
              <button
                type='button'
                onMouseEnter={() => setBreakdownOpen(true)}
                onMouseLeave={() => setBreakdownOpen(false)}
                className='flex items-center bg-transparent border-0 p-0 cursor-pointer'
              >
                <PopoverTrigger
                  render={
                    <button
                      type='button'
                      className='text-muted-foreground hover:text-foreground transition-colors'
                    />
                  }
                >
                  <FontAwesomeIcon icon={faCircleInfo} className='size-3.5' />
                </PopoverTrigger>
              </button>
              <PopoverContent side='bottom' align='start' className='w-64 p-4'>
                <p className='text-sm font-bold mb-4'>
                  {getTranslation('monthlyCredits', {
                    consumed: credits.consumed,
                    total: credits.consumed + credits.balance,
                  })}
                </p>
                <div className='space-y-3'>
                  <div className='flex justify-between'>
                    <span className='text-sm text-muted-foreground'>
                      {getTranslation('balance')}
                    </span>
                    <span className='text-sm'>{credits.balance}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-sm text-muted-foreground'>
                      {getTranslation('consumed')}
                    </span>
                    <span className='text-sm'>{credits.consumed}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-sm text-muted-foreground'>
                      {getTranslation('available')}
                    </span>
                    <span className='text-sm text-ai font-medium'>
                      {credits.balance}
                    </span>
                  </div>
                  {credits.resetsAt && (
                    <p className='text-xs text-muted-foreground pt-1'>
                      {getTranslation('resets', {
                        date: new Date(credits.resetsAt).toLocaleDateString(
                          undefined,
                          { month: 'short', day: 'numeric', year: 'numeric' },
                        ),
                      })}
                    </p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
        <span className='text-sm font-semibold'>
          {credits
            ? getTranslation('creditsLeft', { count: credits.balance })
            : '—'}
        </span>
      </div>
      <div className='h-1.5 w-full rounded-full bg-muted overflow-hidden'>
        <div
          className='h-full rounded-full bg-ai transition-all'
          style={{
            width: credits
              ? `${Math.min(100, (credits.balance / (credits.balance + credits.consumed)) * 100)}%`
              : '0%',
          }}
        />
      </div>
    </div>
  );
}
